"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { ArrowRight, GraduationCap } from "lucide-react"

import { analyticsService, PerformanceResponse } from "@/services/analytics"
import { plannerService, StudyPlan } from "@/services/planner"
import serviceAppsService from "@/services/service-apps"
import { useAuth } from "@/hooks/use-auth"
import { useSidebarMode } from "@/components/providers/sidebar-mode-provider"
import { cn } from "@/lib/utils"
import { interactiveSurface, sectionLabel } from "@/lib/dashboard-ui"

import { GreetingBar } from "@/components/dashboard/home/greeting-bar"
import { StatStrip } from "@/components/dashboard/home/stat-strip"
import { QuickActions } from "@/components/dashboard/home/quick-actions"
import { StudyPlanCard } from "@/components/dashboard/home/study-plan-card"
import { ProjectedScoreCard } from "@/components/dashboard/projected-score-card"
import { ActivityHeatmap } from "@/components/dashboard/activity-heatmap"
import { FeaturedCarousel } from "@/components/dashboard/featured-carousel"
import { CouponCard } from "@/components/dashboard/coupon-card"
import { ScoreBreakdownModal } from "@/components/dashboard/score-breakdown-modal"
import { DailyTimelineModal } from "@/components/dashboard/daily-timeline-modal"
import { AccessModeOnboardingModal } from "@/components/dashboard/access-mode-onboarding-modal"
import { DashboardTour } from "@/components/dashboard/dashboard-tour"
import { TipOfTheDayModal } from "@/components/tip-of-the-day-modal"
import { WhatsNewModal, hasAcknowledgedCurrentRelease } from "@/components/dashboard/whats-new-modal"

const DASHBOARD_TOUR_SEEN_KEY = "dashboard:tour:seen"
const ACCESS_MODE_SEEN_KEY = "access-mode-onboarding-shown"

function isFirstDashboardVisit(): boolean {
    try {
        return !!localStorage.getItem(ACCESS_MODE_SEEN_KEY) && !localStorage.getItem(DASHBOARD_TOUR_SEEN_KEY)
    } catch {
        return false
    }
}

// Mentors and above are shown a floor score so their public profile does not
// undercut them while they are still building attempt history.
const MENTOR_ROLE = 3
const MIN_DISPLAYED_SCORE = 1590
const MIN_DISPLAYED_MATH = 800
const MIN_DISPLAYED_RW = 790

export default function DashboardPage() {
    const { user } = useAuth()
    const { isEasyMode, setIsEasyMode } = useSidebarMode()

    const [stats, setStats] = useState<PerformanceResponse | null>(null)
    const [plan, setPlan] = useState<StudyPlan | null>(null)
    const [loading, setLoading] = useState(true)
    const [planLoading, setPlanLoading] = useState(true)

    const [isScoreModalOpen, setIsScoreModalOpen] = useState(false)
    const [timelineDate, setTimelineDate] = useState<Date | null>(null)
    const [showOnboardingModal, setShowOnboardingModal] = useState(false)
    const [accessModeHighlight, setAccessModeHighlight] = useState(false)
    const [autoStartTour] = useState(isFirstDashboardVisit)
    const [lmsRedirecting, setLmsRedirecting] = useState(false)

    // Hold the tip modal back until the release notes are acknowledged, so two
    // dialogs never stack.
    const [tipUnblocked, setTipUnblocked] = useState(false)
    useEffect(() => setTipUnblocked(hasAcknowledgedCurrentRelease()), [])

    useEffect(() => {
        if (!localStorage.getItem(ACCESS_MODE_SEEN_KEY)) setShowOnboardingModal(true)
    }, [])

    useEffect(() => {
        analyticsService
            .getPerformanceStats(7)
            .then(setStats)
            .catch((error) => console.error("Failed to fetch dashboard stats:", error))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        plannerService
            .getMyPlan()
            .then(setPlan)
            .catch(() => setPlan(null))
            .finally(() => setPlanLoading(false))
    }, [])

    const handleGoToLMS = async () => {
        setLmsRedirecting(true)
        try {
            const { client_id } = await serviceAppsService.lookupByName("learning_management_system")
            const { callback_url } = await serviceAppsService.initiateSession(client_id, "/dashboard")
            window.open(callback_url, "_blank", "noopener,noreferrer")
        } catch (error) {
            console.error("Failed to open the LMS:", error)
        } finally {
            setLmsRedirecting(false)
        }
    }

    const dismissOnboarding = (acceptEasyMode: boolean) => {
        if (acceptEasyMode) setIsEasyMode(true)
        setShowOnboardingModal(false)
        localStorage.setItem(ACCESS_MODE_SEEN_KEY, "1")
        // Point at the switch briefly so the student knows where it lives.
        setTimeout(() => setAccessModeHighlight(true), 300)
        setTimeout(() => setAccessModeHighlight(false), 3800)
    }

    const firstName = user?.full_name?.split(" ")[0] || "Student"

    const isHigherThanMentor = (user?.role ?? 0) > MENTOR_ROLE
    const rawTotal = stats?.overview?.projectedScore || 0
    const rawMath = stats?.overview?.scoreBreakdown?.mathScore || 0
    const rawRw = stats?.overview?.scoreBreakdown?.englishScore || 0
    const belowFloor = isHigherThanMentor && rawTotal < MIN_DISPLAYED_SCORE
    const effectiveTotal = isHigherThanMentor ? Math.max(rawTotal, MIN_DISPLAYED_SCORE) : rawTotal
    const effectiveMath = belowFloor ? MIN_DISPLAYED_MATH : rawMath
    const effectiveRw = belowFloor ? MIN_DISPLAYED_RW : rawRw

    const streak = stats?.overview?.currentStreak ?? 0
    const subline = loading
        ? "Loading your progress…"
        : streak > 1
            ? `You're on a ${streak}-day streak. Keep it going.`
            : ""

    const profileUrl = user
        ? `${process.env.NEXT_PUBLIC_APP_URL || (typeof window !== "undefined" ? window.location.origin : "")}/u/${user.profile_slug || user.id}`
        : null

    return (
        <div className="space-y-8">
            {showOnboardingModal && (
                <AccessModeOnboardingModal
                    onAccept={() => dismissOnboarding(true)}
                    onDecline={() => dismissOnboarding(false)}
                />
            )}

            <GreetingBar
                firstName={firstName}
                subline={subline}
                isEasyMode={isEasyMode}
                onEasyModeChange={setIsEasyMode}
                highlightAccessMode={accessModeHighlight}
                profileUrl={profileUrl}
                onGoToLMS={handleGoToLMS}
                lmsRedirecting={lmsRedirecting}
            />

            <StatStrip overview={stats?.overview} loading={loading} />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="space-y-6 lg:col-span-8">
                    <section id="dashboard-tour-score" aria-labelledby="score-heading">
                        <h2 id="score-heading" className={cn(sectionLabel, "mb-3")}>
                            Where you stand
                        </h2>
                        <ProjectedScoreCard
                            totalScore={effectiveTotal}
                            mathScore={effectiveMath}
                            readingWritingScore={effectiveRw}
                            targetScore={plan?.target_score}
                            loading={loading}
                            isDeveloper={(user?.role ?? 0) >= 7}
                            onShowBreakdown={
                                stats?.overview?.scoreBreakdown ? () => setIsScoreModalOpen(true) : undefined
                            }
                        />
                    </section>

                    <QuickActions isEasyMode={isEasyMode} />

                    {!isEasyMode && (
                        <div id="dashboard-tour-activity">
                            <ActivityHeatmap
                                data={stats?.last30DaysProgress || []}
                                loading={loading}
                                onDayClick={setTimelineDate}
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-6 lg:col-span-4">
                    <div id="dashboard-tour-countdown">
                        <StudyPlanCard plan={plan} loading={planLoading} />
                    </div>

                    <section aria-labelledby="next-step-heading">
                        <h2 id="next-step-heading" className={cn(sectionLabel, "mb-3")}>
                            Looking ahead
                        </h2>
                        <Link href="/tools/university-finder" className={cn(interactiveSurface, "group flex items-center gap-4 p-5")}>
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                <GraduationCap className="h-6 w-6" aria-hidden="true" />
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-1.5">
                                    <span className="text-base font-bold leading-tight text-foreground">
                                        Find universities
                                    </span>
                                    <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                                        New
                                    </span>
                                </span>
                                <span className="mt-0.5 block text-sm text-muted-foreground">
                                    Colleges matched to your profile
                                </span>
                            </span>
                            <ArrowRight
                                className="h-5 w-5 shrink-0 text-muted-foreground motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:translate-x-1"
                                aria-hidden="true"
                            />
                        </Link>
                    </section>

                    <div id="dashboard-tour-lms">
                        <FeaturedCarousel />
                    </div>

                    <CouponCard />
                </div>
            </div>

            <DashboardTour autoStart={autoStartTour} />

            <ScoreBreakdownModal
                isOpen={isScoreModalOpen}
                onClose={() => setIsScoreModalOpen(false)}
                breakdown={stats?.overview.scoreBreakdown}
            />

            {/* Release notes must be acknowledged before anything else pops up. */}
            <WhatsNewModal onAcknowledge={() => setTipUnblocked(true)} />
            {tipUnblocked && <TipOfTheDayModal />}

            <DailyTimelineModal
                isOpen={!!timelineDate}
                onClose={() => setTimelineDate(null)}
                date={timelineDate}
            />
        </div>
    )
}
